/**
 * Variant selector enhancements (product pages)
 *
 * 1. Option filter: for each option (in order), only offer the values that
 *    exist for the options selected before it. When the current value is no
 *    longer offered, the first valid one is selected through the theme's own
 *    handlers (price, SKU, stock and main image stay in sync).
 * 2. Sold-out values are greyed (and tagged) but stay selectable, so the
 *    customer can still see the product, its price and its photos.
 *    "Sold out" = variant.available is false (no stock and not sold when
 *    out of stock). Native dropdowns ignore option colours on macOS/iOS, so
 *    dropdown options are shown through a custom listbox kept in sync with
 *    the native <select> (which stays in the DOM, hidden, and keeps firing
 *    the theme's own change handlers). Set CUSTOM_DROPDOWN to false to go
 *    back to the native list.
 * 3. Carousel (only when every variant has its own, distinct image): the
 *    thumbnails are the images of the variants sharing the first option
 *    (the category). Clicking a thumbnail selects the variant.
 * 4. Description images (sections/product-features.liquid renders the first
 *    ones server side): they follow the selected variant, using the same
 *    ordering rules as the Liquid code.
 *
 * Works with the dropdown, buttons and swatches variant styles. Any failure
 * leaves the default selector untouched.
 *
 * Data: window.themeVariantData (printed by layout/theme.liquid) holds, per
 * variant, its options, availability and featured image; the product JSON
 * (/products/<handle>.js) is only used as a fallback.
 */
(function () {
  'use strict';

  var GREY = '#9a9a9a';
  var CUSTOM_DROPDOWN = true;
  var SOLD_OUT_SUFFIX = ' (\u00e9puis\u00e9)';

  var container = document.querySelector('[data-product-variants]');
  if (!container) return;

  // Variant data is printed in the page by layout/theme.liquid (no extra request).
  // Liquid truncates products with 250+ variants, so those use the product JSON instead.
  var inline = window.themeVariantData;
  if (inline && inline.variants && inline.variants.length && inline.variants.length < 250) {
    init(inline);
  } else {
    loadProductJson();
  }

  function loadProductJson() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!match) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'products/' + match[1] + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('product json ' + response.status);
        return response.json();
      })
      .then(init)
      .catch(function () { /* keep the default selector */ });
  }

  function withWidth(url, width) {
    try {
      var u = new URL(url, window.location.href);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function injectStyles() {
    var style = document.createElement('style');
    style.setAttribute('data-variant-selector-styles', '');
    style.textContent =
      '.product-option__select.is-unavailable{color:' + GREY + '}' +
      '.product-option__select option.is-unavailable{color:' + GREY + '}' +
      '.product-option__button.is-unavailable,.product-option__swatch.is-unavailable{opacity:.45}' +
      '.product-option__button.is-unavailable.active,.product-option__swatch.is-unavailable.active{opacity:.75}' +
      '.product-gallery__thumbnail.is-unavailable img{opacity:.4;filter:grayscale(1)}' +
      '.vs-native{position:absolute!important;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;clip:rect(0 0 0 0)}' +
      '.vs-dropdown{position:relative;width:100%}' +
      '.vs-trigger{display:flex;align-items:center;justify-content:space-between;gap:.75rem;width:100%;padding:.625rem .875rem;' +
        'border:2px solid var(--color-border);border-radius:var(--radius-md);background:#fff;color:var(--color-text);' +
        'font:inherit;font-size:.9375rem;text-align:left;cursor:pointer;transition:border-color var(--transition-speed)}' +
      '.vs-trigger:hover,.vs-trigger:focus-visible,.vs-dropdown.is-open .vs-trigger{border-color:var(--color-primary);outline:none}' +
      '.vs-trigger.is-unavailable{color:' + GREY + '}' +
      '.vs-trigger__text{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      '.vs-trigger__chevron{flex-shrink:0;transition:transform .2s}' +
      '.vs-dropdown.is-open .vs-trigger__chevron{transform:rotate(180deg)}' +
      '.vs-list{display:none;position:absolute;left:0;right:0;top:calc(100% + 4px);z-index:30;max-height:min(55vh,320px);' +
        'overflow-y:auto;margin:0;padding:.25rem;list-style:none;background:#fff;border:2px solid var(--color-primary);' +
        'border-radius:var(--radius-md);box-shadow:0 8px 24px rgba(0,0,0,.12)}' +
      '.vs-dropdown.is-up .vs-list{top:auto;bottom:calc(100% + 4px)}' +
      '.vs-dropdown.is-open .vs-list{display:block}' +
      '.vs-option{padding:.5rem .75rem;border-radius:4px;cursor:pointer;font-size:.9375rem;color:var(--color-text)}' +
      '.vs-option:hover,.vs-option:focus{background:color-mix(in srgb,var(--color-primary) 14%,white);outline:none}' +
      '.vs-option[aria-selected="true"]{font-weight:700}' +
      '.vs-option.is-unavailable{color:' + GREY + '}';
    document.head.appendChild(style);
  }

  function init(product) {
    var optionCount = product.options ? product.options.length : 0;
    if (!optionCount) return;

    var variants = product.variants;
    var variantsById = {};
    variants.forEach(function (variant) { variantsById[variant.id] = variant; });
    var busy = false;

    injectStyles();

    /* ---------- Controls, by option position (1-based) ---------- */

    var selects = {};
    Array.prototype.forEach.call(container.querySelectorAll('[data-option-select]'), function (select) {
      var position = parseInt(select.getAttribute('data-option-position'), 10);
      var all = Array.prototype.slice.call(select.options);
      all.forEach(function (option) {
        option.setAttribute('data-label', option.textContent.trim());
      });
      selects[position] = { el: select, all: all };
    });

    function buttonsFor(position) {
      return container.querySelectorAll('[data-option-button][data-option-position="' + position + '"]');
    }

    function getSelected(position) {
      if (selects[position]) return selects[position].el.value;
      var active = container.querySelector('[data-option-button][data-option-position="' + position + '"].active');
      return active ? active.getAttribute('data-option-value') : null;
    }

    function currentSelection() {
      var selected = [];
      for (var position = 1; position <= optionCount; position++) selected.push(getSelected(position));
      return selected;
    }

    /* ---------- Option filter ---------- */

    // Values of option `position` that exist given the options chosen before it
    function validValues(position, selected) {
      var valid = {};
      variants.forEach(function (variant) {
        for (var i = 0; i < position - 1; i++) {
          if (variant.options[i] !== selected[i]) return;
        }
        valid[variant.options[position - 1]] = true;
      });
      return valid;
    }

    function applyVisibility(position, valid) {
      var info = selects[position];
      if (info) {
        var keep = info.all.filter(function (option) { return valid[option.value]; });
        var present = Array.prototype.slice.call(info.el.options);
        var same = keep.length === present.length && keep.every(function (option, i) { return option === present[i]; });
        if (same) return;
        // Rebuild the list: hiding <option> is not supported by every mobile browser
        info.el.textContent = '';
        keep.forEach(function (option) { info.el.appendChild(option); });
        return;
      }
      Array.prototype.forEach.call(buttonsFor(position), function (button) {
        var isValid = !!valid[button.getAttribute('data-option-value')];
        button.hidden = !isValid;
        button.style.display = isValid ? '' : 'none';
      });
    }

    // Select a value through the theme's own listeners
    function choose(position, value) {
      busy = true;
      try {
        var info = selects[position];
        if (info) {
          var exists = Array.prototype.some.call(info.el.options, function (option) { return option.value === value; });
          if (!exists) return;
          info.el.value = value;
          info.el.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        Array.prototype.forEach.call(buttonsFor(position), function (button) {
          if (button.getAttribute('data-option-value') === value) button.click();
        });
      } finally {
        busy = false;
      }
    }

    /* ---------- Sold-out values: grey, but still selectable ---------- */

    // Values of option `position` offering at least one purchasable variant
    function availableValues(position, selected) {
      var free = {};
      variants.forEach(function (variant) {
        if (!variant.available) return;
        for (var i = 0; i < position - 1; i++) {
          if (variant.options[i] !== selected[i]) return;
        }
        free[variant.options[position - 1]] = true;
      });
      return free;
    }

    function markAvailability(selected) {
      for (var position = 1; position <= optionCount; position++) {
        var free = availableValues(position, selected);
        var info = selects[position];
        if (info) {
          info.all.forEach(function (option) {
            var ok = !!free[option.value];
            var label = option.getAttribute('data-label') + (ok ? '' : SOLD_OUT_SUFFIX);
            if (option.textContent !== label) option.textContent = label;
            option.classList.toggle('is-unavailable', !ok);
            option.style.color = ok ? '' : GREY;
          });
          info.el.classList.toggle('is-unavailable', !free[selected[position - 1]]);
        } else {
          Array.prototype.forEach.call(buttonsFor(position), function (button) {
            button.classList.toggle('is-unavailable', !free[button.getAttribute('data-option-value')]);
          });
        }
      }
    }

    /* ---------- Custom dropdown: sold-out values grey on every platform ---------- */

    var customs = {};

    function closeList(c, restoreFocus) {
      if (!c.isOpen) return;
      c.isOpen = false;
      c.wrap.classList.remove('is-open', 'is-up');
      c.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) c.trigger.focus();
    }

    function closeAll(except) {
      Object.keys(customs).forEach(function (key) {
        if (customs[key] !== except) closeList(customs[key], false);
      });
    }

    function focusItem(c, item) {
      if (!item) return;
      item.focus({ preventScroll: true });
      if (item.offsetTop < c.list.scrollTop) {
        c.list.scrollTop = item.offsetTop;
      } else if (item.offsetTop + item.offsetHeight > c.list.scrollTop + c.list.clientHeight) {
        c.list.scrollTop = item.offsetTop + item.offsetHeight - c.list.clientHeight;
      }
    }

    function openList(c) {
      closeAll(c);
      if (c.isOpen) return;
      c.isOpen = true;
      var rect = c.trigger.getBoundingClientRect();
      var below = window.innerHeight - rect.bottom;
      c.wrap.classList.toggle('is-up', below < 260 && rect.top > below);
      c.wrap.classList.add('is-open');
      c.trigger.setAttribute('aria-expanded', 'true');
      var selected = c.list.querySelector('[aria-selected="true"]') || c.list.firstChild;
      if (selected) {
        c.list.scrollTop = selected.offsetTop - (c.list.clientHeight - selected.offsetHeight) / 2;
        selected.focus({ preventScroll: true });
      }
    }

    function pickOption(c, value) {
      closeList(c, true);
      if (c.info.el.value === value) return;
      c.info.el.value = value;
      c.info.el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function syncCustom(position) {
      var c = customs[position];
      if (!c) return;
      var el = c.info.el;
      c.list.textContent = '';
      Array.prototype.forEach.call(el.options, function (option) {
        var item = document.createElement('li');
        item.className = 'vs-option' + (option.classList.contains('is-unavailable') ? ' is-unavailable' : '');
        item.setAttribute('role', 'option');
        item.setAttribute('tabindex', '-1');
        item.setAttribute('data-value', option.value);
        item.setAttribute('aria-selected', option.value === el.value ? 'true' : 'false');
        item.textContent = option.textContent;
        c.list.appendChild(item);
      });
      var selected = el.options[el.selectedIndex];
      c.text.textContent = selected ? selected.textContent : '';
      c.trigger.classList.toggle('is-unavailable', !!selected && selected.classList.contains('is-unavailable'));
    }

    function syncAllCustom() {
      Object.keys(customs).forEach(function (position) { syncCustom(position); });
    }

    function buildCustom(position) {
      var info = selects[position];
      var wrap = document.createElement('div');
      wrap.className = 'vs-dropdown';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'vs-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      var text = document.createElement('span');
      text.className = 'vs-trigger__text';
      trigger.appendChild(text);
      trigger.insertAdjacentHTML('beforeend',
        '<svg class="vs-trigger__chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">' +
        '<path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>');

      var list = document.createElement('ul');
      list.className = 'vs-list';
      list.setAttribute('role', 'listbox');

      wrap.appendChild(trigger);
      wrap.appendChild(list);
      info.el.classList.add('vs-native');
      info.el.setAttribute('tabindex', '-1');
      info.el.setAttribute('aria-hidden', 'true');
      info.el.parentNode.insertBefore(wrap, info.el.nextSibling);

      var c = { info: info, wrap: wrap, trigger: trigger, text: text, list: list, isOpen: false };
      customs[position] = c;

      trigger.addEventListener('click', function () {
        if (c.isOpen) closeList(c, false); else openList(c);
      });
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          openList(c);
        }
      });
      list.addEventListener('click', function (event) {
        var item = event.target.closest && event.target.closest('.vs-option');
        if (item) pickOption(c, item.getAttribute('data-value'));
      });
      list.addEventListener('keydown', function (event) {
        var items = Array.prototype.slice.call(list.children);
        var index = items.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          focusItem(c, items[Math.min(index + 1, items.length - 1)]);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          focusItem(c, items[Math.max(index - 1, 0)]);
        } else if (event.key === 'Home') {
          event.preventDefault();
          focusItem(c, items[0]);
        } else if (event.key === 'End') {
          event.preventDefault();
          focusItem(c, items[items.length - 1]);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (index >= 0) pickOption(c, items[index].getAttribute('data-value'));
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeList(c, true);
        } else if (event.key === 'Tab') {
          closeList(c, false);
        } else if (event.key.length === 1) {
          var letter = event.key.toLowerCase();
          for (var step = 1; step <= items.length; step++) {
            var candidate = items[(index + step) % items.length];
            if (candidate.textContent.trim().toLowerCase().indexOf(letter) === 0) {
              focusItem(c, candidate);
              break;
            }
          }
        }
      });
      syncCustom(position);
    }

    document.addEventListener('mousedown', function (event) {
      Object.keys(customs).forEach(function (key) {
        if (!customs[key].wrap.contains(event.target)) closeList(customs[key], false);
      });
    });

    if (CUSTOM_DROPDOWN) {
      Object.keys(selects).forEach(function (position) { buildCustom(position); });
    }

    function refresh() {
      var selected = [];
      for (var position = 1; position <= optionCount; position++) {
        var valid = validValues(position, selected);
        var current = getSelected(position); // read before the list is rebuilt
        applyVisibility(position, valid);

        if (current === null || !valid[current]) {
          var values = product.options[position - 1].values;
          var fallback = null;
          for (var i = 0; i < values.length; i++) {
            if (valid[values[i]]) { fallback = values[i]; break; }
          }
          if (fallback !== null) {
            choose(position, fallback);
            current = fallback;
          }
        } else if (selects[position]) {
          selects[position].el.value = current;
        }
        selected.push(current);
      }
      markAvailability(selected);
      syncAllCustom();
    }

    function selectVariant(variant) {
      for (var position = 1; position <= optionCount; position++) {
        var value = variant.options[position - 1];
        if (getSelected(position) !== value) choose(position, value);
      }
    }

    /* ---------- Carousel: images of the variants of the selected category ---------- */

    function oneImagePerVariant() {
      if (optionCount < 2) return false;
      var seen = {};
      return variants.every(function (variant) {
        var id = variant.featured_image && variant.featured_image.id;
        if (!id || seen[id]) return false;
        seen[id] = true;
        return true;
      });
    }

    var imagesMode = oneImagePerVariant();
    var strip = document.querySelector('[data-thumbnails]');
    var stripCategory = null;

    function variantLabel(variant) {
      var parts = variant.options.slice(1);
      return (parts.length ? parts : variant.options).join(' / ');
    }

    function buildThumb(variant, current) {
      var button = document.createElement('button');
      var label = variantLabel(variant) + (variant.available ? '' : SOLD_OUT_SUFFIX);
      button.type = 'button';
      button.className = 'product-gallery__thumbnail' +
        (variant.id === current.id ? ' active' : '') +
        (variant.available ? '' : ' is-unavailable');
      button.setAttribute('data-variant-thumb', variant.id);
      button.setAttribute('aria-label', label);
      button.title = label;

      var img = document.createElement('img');
      img.src = withWidth(variant.featured_image.src, 180);
      img.alt = variant.featured_image.alt || label;
      img.width = 90;
      img.height = 90;
      img.loading = 'lazy';
      button.appendChild(img);

      button.addEventListener('click', function () { selectVariant(variant); });
      return button;
    }

    function centerActiveThumb() {
      var active = strip.querySelector('.active');
      if (!active) return;
      var delta = active.getBoundingClientRect().left - strip.getBoundingClientRect().left;
      strip.scrollTo({ left: strip.scrollLeft + delta - (strip.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
    }

    function syncThumbs(current) {
      if (!imagesMode || !strip) return;
      if (stripCategory !== current.options[0]) {
        stripCategory = current.options[0];
        strip.textContent = '';
        variants.forEach(function (variant) {
          if (variant.options[0] === stripCategory) strip.appendChild(buildThumb(variant, current));
        });
      } else {
        Array.prototype.forEach.call(strip.querySelectorAll('[data-variant-thumb]'), function (button) {
          button.classList.toggle('active', parseInt(button.getAttribute('data-variant-thumb'), 10) === current.id);
        });
      }
      centerActiveThumb();
    }

    /* ---------- Description images follow the selected variant ---------- */

    // Same rules as sections/product-features.liquid: other designs of the
    // selected category first (after the selected one, then before it), then the
    // other categories, purchasable ones before sold-out ones.
    function descriptionOrder(current) {
      var same = variants.filter(function (variant) { return variant.options[0] === current.options[0]; });
      var index = 0;
      same.forEach(function (variant, i) { if (variant.id === current.id) index = i; });
      var ordered = same.slice(index + 1).concat(same.slice(0, index));
      variants.forEach(function (variant) {
        if (variant.options[0] !== current.options[0]) ordered.push(variant);
      });
      var inStock = ordered.filter(function (variant) { return variant.available; });
      var soldOut = ordered.filter(function (variant) { return !variant.available; });
      return inStock.concat(soldOut);
    }

    function syncDescriptionImages(current) {
      var images = document.querySelectorAll('[data-variant-feature]');
      if (!images.length) return;
      var order = descriptionOrder(current);
      Array.prototype.forEach.call(images, function (img) {
        var variant = order[parseInt(img.getAttribute('data-variant-feature'), 10)];
        if (!variant || !variant.featured_image) return;
        var src = variant.featured_image.src;
        img.src = withWidth(src, 600);
        img.srcset = [400, 600, 800].map(function (w) { return withWidth(src, w) + ' ' + w + 'w'; }).join(', ');
        img.alt = variant.featured_image.alt || 'D\u00e9tail produit';
      });
    }

    /* ---------- Wiring ---------- */

    window.addEventListener('variantChange', function (event) {
      var variant = event.detail && event.detail.variant && variantsById[event.detail.variant.id];
      if (!variant) return;
      markAvailability(currentSelection());
      syncAllCustom();
      syncThumbs(variant);
      syncDescriptionImages(variant);
    });

    function onUserChange() {
      if (busy) return;
      refresh();
    }

    // Bubbling listeners on the container run after the theme's own handlers
    container.addEventListener('change', onUserChange);
    container.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-option-button]')) onUserChange();
    });

    refresh();

    var idInput = document.querySelector('input[data-variant-id]');
    var initial = (idInput && variantsById[parseInt(idInput.value, 10)]) || variants[0];
    syncThumbs(initial);
  }
})();
